/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_TRANSACTION_DETAIL } from "../graphql";
import { VApplication } from "../../../application";
import { usePolicyId } from "../provider";
import config from "../config/featuresSection.config.json";
import moment from 'moment';

import { AppNavigatorService } from "../../appNavigatorService";
import { toHeaderCase } from "js-convert-case";


export interface IFeatureSectionMethods extends IPluginMethodMap {
  getFeatureSectionProps(args: any): Promise<any>;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const lookup = require("country-code-lookup");

export class FeatureSectionService implements IAgentPlugin {
  readonly methods: IFeatureSectionMethods;

  constructor() {
    this.methods = {
      getFeatureSectionProps: this.getProps.bind(this),
    };
  }

  async getProps(args: any) {
    const { hash } = usePolicyId();

    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const res = await client.query({
          query: GET_TRANSACTION_DETAIL,
          variables: {
            input: {
              hash: hash,
            }
          },
          fetchPolicy: "network-only",
        });
        if (res?.data?.getBlockchianTxn) {
          console.log(`transaction details`, res?.data?.getBlockchianTxn);
          let _result = res?.data?.getBlockchianTxn;
          return {
            title:config?.title,
            heroImage:config?.heroImage,
            buttons:[
              {
                  ...config?.buttons,
                  link:_result?.txnLink
              }
            ],
            listItems:this.getListItems(_result),
            carouselItems: this.getCarouselItems(_result),
            accordionItems:this.getAccordionItems(_result),
          };
        }
      }
    } catch (err: any) {
      console.log(`Sign In user error`, err?.response?.data);
      throw Error(err?.response?.data?.error?.display_message);
    }
    return { ...(config || {}) };
  }

  private getAccordionItems(result:any){
    let temp:any = [];
    let _tempObj = {...result};
    delete _tempObj.value;
    delete _tempObj.txnLink;
    for(const [key, value] of Object.entries(_tempObj)){
      if(key ==="timestamp"){
        temp.push({
          title: toHeaderCase(key),
          value: moment(value).format("LLLL")
        });
      }
      else {
        temp.push({
        title: toHeaderCase(key),
        value:value
      });
    }
    }
    return [
      {
        type:"list",
        title:"Overview",
        data:temp
      }
    ];
  }

  private getCarouselItems(result: any) {
    let _temp = [];
    _temp.push({
      type:"qr",
      src:result?.txnLink,
      poster:config?.qrPosterImg
    });
    return _temp;
  }

  private getListItems(res: any) {
    let _temp = config?.listItems?.map((ele: any) => {
      if (ele?.key === "from") {
        return {
          ...ele,
          subTitle: res?.from ?? "-",
        };
      }
      if (ele?.key === "status") {
        return {
          ...ele,
          subTitle: res?.status ?? "-",
        };
      }
      if (ele?.key === "transactionHash") {
        return {
          ...ele,
          subTitle: res?.hash ?? "-",
        };
      }
      if (ele?.key === "timestamp") {
        return {
          ...ele,
          subTitle: moment(res?.timestamp).format('LLLL') ?? "-"
        };
      }
      if (ele?.key === "interactedWith") {
          return {
            ...ele,
            subTitle: res?.to ?? "-"
          };
        }
        if (ele?.key === "value") {
          return {
            ...ele,
            subTitle: res?.value ?? "-"
          };
        }
        if (ele?.key === "transactionId") {
          return {
            ...ele,
            subTitle: res?.txnId ?? "-"
          };
        }
    });

    return _temp;
  }
}
